/**
 * Do'stlar va yozishma — docs/API.md §9.
 *
 * Tiplar `@voice/api-client` dan kelishi kerak edi, lekin ular
 * generatsiya qilinganda `components["schemas"][...]` ichida turadi va
 * har ishlatishda uzun yo'l yozishga majbur qiladi. Shuning uchun shu
 * yerda qisqa nom beriladi — qiymat SERVERDAN, shakl esa o'sha.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { request } from './client';

export type Presence = 'offline' | 'online' | 'live' | 'busy';

export interface FriendRow {
  friendship_id: number;
  user_id: number;
  first_name: string;
  palette_idx: number;
  presence: Presence;
  status: string;
  incoming: boolean;
  created_at: string;
}

export interface FriendsPage {
  friends: FriendRow[];
  incoming_requests: FriendRow[];
}

export interface DmThreadRow {
  thread_id: number;
  user_id: number;
  first_name: string;
  palette_idx: number;
  presence: Presence;
  last_message: string | null;
  last_message_at: string | null;
  unread: number;
}

/** Javob berilgan xabarning iqtibosi — pufak ustidagi ikki qator. */
export interface DmReplyPreview {
  id: number;
  text: string | null;
  /** Asl xabar MENIKIMI. Ism yuborilmaydi: ipda ikki kishi bor. */
  mine: boolean;
}

export interface DmMessageRow {
  id: number;
  sender_id: number;
  text: string | null;
  created_at: string;
  read_at: string | null;
  mine: boolean;
  /** Javob bo'lsa iqtibos. Asl xabar o'chirilgan bo'lsa `null`. */
  reply_to: DmReplyPreview | null;
}

export const FRIENDS_KEY = ['friends'] as const;
export const THREADS_KEY = ['dm', 'threads'] as const;

export function messagesKey(threadId: number) {
  return ['dm', 'messages', threadId] as const;
}

/**
 * Do'stlar va so'rovlar.
 *
 * `staleTime` qisqa: presence tez o'zgaradi va eskirgan yashil nuqta
 * "onlayn" deb aldab qo'yardi.
 */
export function useFriends(enabled = true) {
  return useQuery({
    queryKey: FRIENDS_KEY,
    queryFn: () => request<FriendsPage>('/friends'),
    enabled,
    staleTime: 15_000,
    retry: false,
  });
}

export function useSendFriendRequest() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (userId: number) => request<FriendRow>(`/friends/${userId}`, { method: 'POST' }),
    onSuccess: () => client.invalidateQueries({ queryKey: FRIENDS_KEY }),
  });
}

export function useRespondToRequest() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: { id: number; action: 'accept' | 'reject' }) =>
      request<{ ok: boolean }>(`/friends/${id}`, { method: 'PATCH', body: { action } }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: FRIENDS_KEY });
      // Qabul qilingan so'rov yangi suhbat ochish imkonini beradi.
      void client.invalidateQueries({ queryKey: THREADS_KEY });
    },
  });
}

export function useThreads(enabled = true) {
  return useQuery({
    queryKey: THREADS_KEY,
    queryFn: () => request<{ items: DmThreadRow[] }>('/dm/threads'),
    enabled,
    staleTime: 10_000,
    retry: false,
  });
}

export function useOpenThread() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (userId: number) =>
      request<{ items: DmThreadRow[] }>('/dm/threads', {
        method: 'POST',
        body: { user_id: userId },
      }),
    onSuccess: (page) => client.setQueryData(THREADS_KEY, page),
  });
}

/**
 * Xabarlar.
 *
 * Bu so'rov SERVERDA o'qilgan deb belgilaydi ham — alohida "o'qildi"
 * chaqiruvi yo'q. Shuning uchun muvaffaqiyatda ro'yxat keshini ham
 * yangilaymiz: o'qilmagan hisoblagichi darhol tushishi kerak.
 */
export function useMessages(threadId: number, enabled = true) {
  const client = useQueryClient();
  return useQuery({
    queryKey: messagesKey(threadId),
    queryFn: async () => {
      const page = await request<{ items: DmMessageRow[]; next_cursor: number | null }>(
        `/dm/threads/${threadId}/messages?limit=50`,
      );
      void client.invalidateQueries({ queryKey: THREADS_KEY });
      return page;
    },
    enabled,
    staleTime: 5_000,
    retry: false,
  });
}

/**
 * Xabar yuborish.
 *
 * `replyToId` ixtiyoriy. Server uning AYNI shu ipda ekanini tekshiradi
 * va javobda iqtibosni qaytaradi — klient iqtibos matnini o'zi
 * yasamaydi.
 */
export function useSendMessage(threadId: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ text, replyToId }: { text: string; replyToId?: number }) =>
      request<DmMessageRow>(`/dm/threads/${threadId}/messages`, {
        method: 'POST',
        body: replyToId ? { text, reply_to_id: replyToId } : { text },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: messagesKey(threadId) });
      void client.invalidateQueries({ queryKey: THREADS_KEY });
    },
  });
}

/** Shikoyat — uch bosish (SPEC, faza 3 qabul mezoni). */
export interface ReportBody {
  target_user_id: number;
  context: 'room' | 'call' | 'dm' | 'profile';
  reason?: string;
  room_id?: number;
  comment?: string;
}

export function useSendReport() {
  return useMutation({
    mutationFn: (body: ReportBody) =>
      request<{ id: number; status: string }>('/reports', { method: 'POST', body }),
  });
}
